import environments from './XEnvironment.js';
import { XTemplateDOM } from './XDOM.js';
import { xArray, xBoolean, xInteger, xObject, xString } from './XTypes.js';
import { xBase64ToObject, xObjectToBase64 } from './XUtil.js';

import template from './XVideo.html?raw';

import Plyr from 'plyr';

export class XVideo
{
    static get type () { return 'XVideo'; }

    static #extensionPriority = ['webm', 'mp4'];

    static detectAvailableExtensions()
    {
        console.log('TODO');
    }

    #dom;
    #parent;
    #element;
    #color;
    #urlAsset;
    #extensions;
    #resolutions;
    #ratio;
    #plyr = null;

    constructor(dom, parent, {
        resizeContainer = environments.active.resizeContainer,
    } = {})
    {
        this.#dom     = dom;
        this.#parent  = parent;
        this.#element = this.#dom.video._element;
        const data = Object.assign({
            color       : '#ffffff',
            urlAsset    : '',
            extensions  : [],
            resolutions : [],
            ratio       : '',
        }, xBase64ToObject(this.#dom._element.dataset.xData));
        this.#color       = data.color;
        this.#urlAsset    = data.urlAsset;
        this.#extensions  = data.extensions;
        this.#resolutions = data.resolutions;
        this.#ratio       = data.ratio;
        resizeContainer.add(this);
    };

    setEmptyVideo()
    {
        console.log('TODO');
    }

    updateVideo(destroy=false)
    {
        console.log('updateVideo');
        if(!this.#urlAsset)
        {
            this.setEmptyVideo();
            return;
        }
        let extension = null;
        for(const extensionPriority of XVideo.#extensionPriority)
        {
            if(this.#extensions?.includes(extensionPriority))
            {
                extension = extensionPriority;
                break;
            }
        }
        if(!extension)
        {
            this.setEmptyVideo();
            return;
        }
        let bestResolution = null;
        if(this.#resolutions.length > 0)
        {
            const boundingClientRect = this.#element.getBoundingClientRect();
            const width = boundingClientRect.width * window.devicePixelRatio;
            const height = boundingClientRect.height * window.devicePixelRatio;
            let i;
            for(i = 0; i < this.#resolutions.length-1; i++)
            {
                if(width <= this.#resolutions[i].width || height <= this.#resolutions[i].height)
                {
                    break;
                }
            }
            bestResolution = this.#resolutions[i];
        }
        const src = this.#urlAsset + String(bestResolution.width) + 'x' + String(bestResolution.height) + '.' + extension;
        if(destroy)
        {
            this.#plyr?.destroy();
            this.#plyr = null;
        }
        if(!this.#plyr)
        {
            const qualityLabel = {};
            const qualityBadge = {};
            for(const resolution of this.#resolutions)
            {
                const min = Math.min(resolution.width, resolution.height);
                const label = String(resolution.width) + 'x' + String(resolution.height);
                qualityLabel[resolution.height] = label;
                if(min >= 2160)
                {
                    qualityBadge[resolution.height] = '4K';
                }
                else if(min >= 720)
                {
                    qualityBadge[resolution.height] = 'HD';
                }
                else if(min >= 480)
                {
                    qualityBadge[resolution.height] = 'SD';
                }
            }
            this.#plyr = new Plyr(this.#element.firstElementChild, {
                settings: [
                    'quality'
                ],
                invertTime: false,
                controls: [
                    'play-large', // The large play button in the center
                    //'restart', // Restart playback
                    //'rewind', // Rewind by the seek time (default 10 seconds)
                    'play', // Play/pause playback
                    //'fast-forward', // Fast forward by the seek time (default 10 seconds)
                    'progress', // The progress bar and scrubber for playback and buffering
                    'current-time', // The current time of playback
                    //'duration', // The full duration of the media
                    'mute', // Toggle mute
                    'volume', // Volume control
                    'captions', // Toggle captions
                    'settings', // Settings menu
                    //'pip', // Picture-in-picture (currently Safari only)
                    //'airplay', // Airplay (currently Safari only)
                    //'download', // Show a download button with a link to either the current source or a custom URL you specify in your options
                    'fullscreen', // Toggle fullscreen
                ],
                quality: { 
                    default: bestResolution.height, 
                    options: this.#resolutions.map(resolution => resolution.height)
                },
                i18n: {
                    qualityLabel: qualityLabel,
                    qualityBadge: qualityBadge
                },
                ratio: this.#ratio || null
            });
            this.#plyr.source = {
                type: 'video',
                sources: this.#resolutions.map((resolution) => {
                    return {
                        src: this.#urlAsset + String(resolution.width) + 'x' + String(resolution.height) + '.' + extension,
                        type: 'video/' + extension,
                        size: resolution.height
                    };
                }),
                //poster: '/path/to/poster.jpg',
                previewThumbnails: {
                    enabled: true,
                    src: this.#urlAsset + 'storyboard.vtt'
                }
            };
        }
        if(bestResolution)
        {
            this.#plyr.quality = bestResolution.height;
        }
    }

    _handleResize()
    {
        this.updateVideo();
    }

    _destroy()
    {
        this.#plyr.destroy();
        this.#plyr = null;
        this._resizeContainer?.remove(this);
        this.#dom._destroy();
    }

    set color       (color      ) { this.#color       = color;       }
    set urlAsset    (urlAsset   ) { this.#urlAsset    = urlAsset;    }
    set extensions  (extensions ) { this.#extensions  = extensions;  }
    set resolutions (resolutions) { this.#resolutions = resolutions; this.#resolutions.sort((a,b) => a.width - b.width); }
    set ratio       (ratio      ) { this.#ratio       = ratio;       }

    get _dom        () { return this.#dom;         }
    get parent      () { return this.#parent;      }
    get color       () { return this.#color;       }
    get urlAsset    () { return this.#urlAsset;    }
    get extensions  () { return this.#extensions;  }
    get resolutions () { return this.#resolutions; }
    get ratio       () { return this.#ratio;       }
};

export class XVideoReactive extends XVideo
{
    static get specification () 
    {
        return xObject({
            $: XVideo.type,
            extensions: xArray(xString()),
            alt: xString(),
            name: xString(),
            color: xString('#ffffff'),
            ratio: xString(''),
            duration: xInteger(),
            hasAudio: xBoolean(),
            resolutions: xArray(xObject({
                width: xInteger(),
                height: xInteger()
            }))
        });
    }

    static get template () { return XVideoReactive.#template; }

    static #template;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XVideoReactive.specification, domEnvironment);
    }

    #data;
    #usedTemplate;

    constructor(data, parent, {
        resizeContainer = environments.active.resizeContainer,
        basePath        = environments.active.urlAssets_, 
        template        = XVideoReactive.template
    } = {})
    {
        super(template._createAccessibleDOM(data), parent, {
            resizeContainer: resizeContainer
        });
        this.#data = data;
        this.#usedTemplate = template;
        data._enableParenting();
        this._dom._computed((data, basePath) =>
        {
            const namePath = data.name ? (data.name + '/') : '';
            const versionPath = data._version ? (data._version + '/') : '';
            const keyPath = data._key + '/';
            this.color = data.color;
            this.extensions = data.extensions.toJSON();
            this.resolutions = data.resolutions.toJSON();
            this.ratio = data.ratio;
            this.urlAsset = basePath._value + keyPath + versionPath + namePath;
            this.updateVideo(true);
        }, data, basePath);
        //this.#dom._addAttachment(this.#static);
    };

    _render()
    {
        const dom = this.#usedTemplate._createAccessibleDOM(this.#data);
        dom._element.dataset.xData = xObjectToBase64({
            color       : this.color,
            urlAsset    : this.urlAsset,
            extensions  : this.extensions,
            resolutions : this.resolutions,
            ratio       : this.ratio,
        });
        return dom;
    }

    get data () { return this.#data; }
};